import { InfiniteQueryConfigOptions } from '@reduxjs/toolkit/query';
import { apiSlice } from 'src/app/redux/api/apiSlice';
import { LookupDataType } from 'src/types/query.type';

type LookupResultType = {
  count: number;
  rows: any;
};
export type InitialPageParam = {
  limit: number;
  offset: number;
  order?: string;
  sortOrder?: 'ASC' | 'DESC';
};

const getNextPageParam = <T extends LookupResultType, K extends InitialPageParam, L extends LookupDataType | void>(
  lastPage: T,
  allPages: T[],
  lastPageParam: K,
  allPageParams: K[],
  queryArgs: L,
) => {
  const nextOffset = lastPageParam.offset + lastPageParam.limit;
  const remainingItems = lastPage.count - nextOffset;
  if (remainingItems <= 0) {
    return undefined;
  }

  return {
    ...lastPageParam,
    ...queryArgs,
    offset: nextOffset,
  };
};

const getPreviousPageParam = <T extends LookupResultType, K extends InitialPageParam, L extends LookupDataType | void>(
  firstPage: T,
  allPages: T[],
  firstPageParam: K,
  allPageParams: K[],
  queryArgs: L,
) => {
  const prevOffset = firstPageParam.offset - firstPageParam.limit;
  if (prevOffset < 0) return undefined;

  return {
    ...firstPageParam,
    ...queryArgs,
    offset: firstPageParam.offset - firstPageParam.limit,
  };
};

export const infiniteQueryOptions: InfiniteQueryConfigOptions<any, any, any> = {
  initialPageParam: { limit: 20, offset: 0 },
  getNextPageParam,
  getPreviousPageParam,
};

export const onQueryStartedCreate = async <T>(
  data: T,
  { dispatch, queryFulfilled, getState }: any,
  queryKey: string,
) => {
  try {
    const state = getState();
    const { data: createdData } = await queryFulfilled;
    const args = apiSlice.util.selectCachedArgsForQuery(state, queryKey as never);

    (args as any[]).forEach((arg) => {
      dispatch(
        (apiSlice.util.updateQueryData as any)(queryKey, arg, (draft: any) => {
          const sortField = arg?.order;
          const sortOrder = arg?.sortOrder;

          const targetPage = draft.pages[0];
          const { rows } = targetPage;

          if (!sortField || !sortOrder || rows.length === 0) {
            rows.unshift(createdData);
            targetPage.count += 1;
            return;
          }

          const compare = (a: any, b: any) => {
            const valA = a?.[sortField];
            const valB = b?.[sortField];

            if (valA == null || valB == null) return 0;

            if (typeof valA === 'string' && typeof valB === 'string') {
              return sortOrder === 'ASC' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }

            const aNum = new Date(valA).getTime?.() || Number(valA);
            const bNum = new Date(valB).getTime?.() || Number(valB);

            return sortOrder === 'ASC' ? aNum - bNum : bNum - aNum;
          };

          let insertIndex = rows.findIndex((row: any) => compare(createdData, row) < 0);
          if (insertIndex === -1) insertIndex = rows.length;

          rows.splice(insertIndex, 0, createdData);
          targetPage.count += 1;
        }),
      );
    });
  } catch (error) {
    console.log('error', error);
  }
};

export const onQueryStartedUpdate = async (
  { id }: { id: string },
  { dispatch, queryFulfilled, getState }: any,
  queryKey: string,
) => {
  try {
    const state = getState();
    const { data: updatedData } = await queryFulfilled;
    const args = apiSlice.util.selectCachedArgsForQuery(state, queryKey as never);

    args.forEach((arg) => {
      dispatch(
        (apiSlice.util.updateQueryData as any)(queryKey, arg, (draft: any) => {
          for (const page of draft.pages) {
            const index = page.rows.findIndex((obj: any) => obj.id === id);
            if (index !== -1) {
              page.rows[index] = updatedData;
              break;
            }
          }
        }),
      );
    });
  } catch (err) {
    console.error('Cache update failed after update:', err);
  }
};

export const onQueryStartedDelete = async (
  id: string,
  { dispatch, queryFulfilled, getState }: any,
  queryKey: string,
) => {
  try {
    const state = getState();
    await queryFulfilled;
    const args = apiSlice.util.selectCachedArgsForQuery(state, queryKey as never);

    args.forEach((arg) => {
      dispatch(
        (apiSlice.util.updateQueryData as any)(queryKey, arg, (draft: any) => {
          for (const page of draft.pages) {
            const index = page.rows.findIndex((person: any) => person.id === id);
            if (index !== -1) {
              page.rows.splice(index, 1);
              page.count -= 1;
              break;
            }
          }
        }),
      );
    });
  } catch (err) {
    console.error('Cache update failed after update:', err);
  }
};

export const updateCacheCreateData = (queryName: string, createdData: any, state: any) => {
  const args = apiSlice.util.selectCachedArgsForQuery(state, queryName as never);
  return (apiSlice.util.updateQueryData as any)(queryName, args, (draft: any) => {
    if (draft.pages.length > 0) {
      draft.pages[0].rows.unshift(createdData);
      draft.pages[0].count += 1;
    } else {
      draft.pages.push({ data: [createdData], count: 1 });
    }
  });
};

export const updateCacheEditData = (queryName: string, updatedData: any, id: string) => {
  return (apiSlice.util.updateQueryData as any)(queryName, undefined, (draft: any) => {
    for (const page of draft.pages) {
      const index = page.rows.findIndex((data: any) => data.id === id);
      if (index !== -1) {
        page.rows[index] = updatedData;
        break;
      }
    }
  });
};
export const updateCacheDeleteData = (queryName: string, id: string) => {
  return (apiSlice.util.updateQueryData as any)(queryName, undefined, (draft: any) => {
    for (const page of draft.pages) {
      const index = page.rows.findIndex((person: any) => person.id === id);
      if (index !== -1) {
        page.rows.splice(index, 1);
        page.count -= 1;
        break;
      }
    }
  });
};
